import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useImpersonation } from '@/hooks/useImpersonation'
import {
  Moon,
  Sun,
  LogOut,
  Settings,
  User,
  Search,
  Menu,
  Building2,
  Check,
  Copy,
  PlayCircle,
  Shield,
  Sparkles
} from 'lucide-react'
import CommandPalette from './CommandPalette'
import NotificationDropdown from './NotificationDropdown'
import CrossOrgNotifications from './CrossOrgNotifications'
import { Button } from '@/components/ui/button'
import api from '@/lib/api'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { cn } from '@/lib/utils'

interface HeaderProps {
  sidebarCollapsed: boolean
  isMobile?: boolean
  onMenuClick?: () => void
  /** When the calendar peek rail is open, shrink the header to stop at its
      left edge so the rail can render its own header strip cleanly. */
  peekRailOpen?: boolean
}

const PEEK_RAIL_WIDTH_PX = 320

const routeNames: Record<string, string> = {
  '/': 'Dashboard',
  '/chats': 'Live Chats',
  '/conversations': 'History',
  '/analytics': 'Analytics',
  '/leads': 'Leads',
  '/calls': 'Calls',
  '/bookings': 'Bookings Calendar',
  '/messages': 'Team Messages',
  '/sms': 'SMS',
  '/team': 'Team & Users',
  '/follow-up-queue': 'Follow-Up Queue',
  '/agents': 'AI Agents',
  '/integrations': 'Integrations',
  '/form-connections': 'Form Connections',
  '/users': 'Users',
  '/widget': 'Widget',
  '/settings': 'Settings',
  '/settings/billing': 'Billing',
  '/settings/billing/plans': 'Plans',
  '/affiliate': 'Affiliate',
  '/admin': 'The Castle',
  '/admin/organizations': 'Organizations',
  '/admin/users': 'All Users',
  '/admin/voice': 'Voice & Costs',
  '/admin/custom-llm-providers': 'Custom LLMs',
  '/admin/access-codes': 'Access Codes',
  '/admin/demo-leads': 'Demo Leads',
  '/admin/demo-dnc': 'Demo DNC',
}

// UUIDs (org IDs, user IDs) shouldn't surface as raw strings in breadcrumbs.
// Match v4-style UUIDs as well as the leading hex chunks we see in our IDs.
const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function prettySegment(segment: string, parentPath: string): string {
  if (UUID_LIKE.test(segment)) {
    if (parentPath === '/admin/organizations') return 'Organization'
    if (parentPath === '/leads') return 'Lead'
    if (parentPath === '/team/profile') return 'Member'
    if (parentPath.startsWith('/agents')) return 'Agent'
    return 'Detail'
  }
  return segment.charAt(0).toUpperCase() + segment.slice(1)
}

export default function Header({
  sidebarCollapsed,
  isMobile = false,
  onMenuClick,
  peekRailOpen = false,
}: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { agent, logout, selectOrganization } = useAuth()
  const { isImpersonating, stopImpersonation } = useImpersonation()
  const isPlatformAdmin = (agent as any)?.isPlatformAdmin
  const isSuperAdmin = (agent as any)?.isSuperAdmin
  type ThemeMode = 'light' | 'midnight' | 'dark'
  const [theme, setTheme] = React.useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-preference')
    if (saved === 'light' || saved === 'midnight' || saved === 'dark') return saved
    if (document.documentElement.classList.contains('midnight')) return 'midnight'
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  })
  const [commandOpen, setCommandOpen] = React.useState(false)
  const [userOrgs, setUserOrgs] = React.useState<any[]>([])
  const [activeOrgId, setActiveOrgId] = React.useState<string | null>(null)
  const [affiliateInfo, setAffiliateInfo] = React.useState<any | null>(null)
  const [switchingOrg, setSwitchingOrg] = React.useState(false)
  const [switchingTo, setSwitchingTo] = React.useState<string | null>(null) // 'admin' or org name

  // Load user's organizations
  React.useEffect(() => {
    const loadOrgs = async () => {
      try {
        const response = await api.get('/api/auth/organizations')
        if (response.data.success) {
          setUserOrgs(response.data.organizations || [])
          setActiveOrgId(response.data.activeOrganizationId || null)
        }
      } catch {
        // Non-critical
      }
    }
    loadOrgs()
  }, [])

  React.useEffect(() => {
    let cancelled = false
    const loadAffiliate = async () => {
      if (!agent?.id) {
        setAffiliateInfo(null)
        return
      }
      try {
        const response = await api.get('/api/affiliates/me')
        if (!cancelled) setAffiliateInfo(response.data)
      } catch {
        if (!cancelled) setAffiliateInfo(null)
      }
    }
    loadAffiliate()
    return () => { cancelled = true }
  }, [agent?.id])

  const handleSwitchOrg = async (orgId: string) => {
    if (switchingOrg) return
    // Skip if already on this org (but NOT if on /admin — always allow switching back)
    if (orgId === activeOrgId && !location.pathname.startsWith('/admin')) return
    const org = userOrgs.find(o => o.id === orgId)
    setSwitchingOrg(true)
    setSwitchingTo(org?.name || 'organization')

    localStorage.removeItem('app_impersonation')
    stopImpersonation()

    try {
      const result = await selectOrganization(orgId)
      if (result.success) {
        window.location.href = '/'
      }
    } finally {
      setSwitchingOrg(false)
      setSwitchingTo(null)
    }
  }

  const handleSwitchToAdmin = (e: React.MouseEvent) => {
    e.preventDefault()
    if (location.pathname.startsWith('/admin')) return // already there
    setSwitchingOrg(true)
    setSwitchingTo('The Castle')
    localStorage.removeItem('app_impersonation')
    stopImpersonation()
    window.location.href = '/admin'
  }

  const handleOpenAffiliateDemo = async () => {
    const sandboxId = affiliateInfo?.affiliate?.sandboxOrganization?.id
    if (!sandboxId) {
      navigate('/affiliate')
      return
    }
    await handleSwitchOrg(sandboxId)
  }

  const handleCopyAffiliateLink = async () => {
    const url = affiliateInfo?.links?.find((link: any) => link.active)?.url || affiliateInfo?.links?.[0]?.url
    if (!url) {
      navigate('/affiliate')
      return
    }
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // Ignore clipboard failures in the account menu; the Affiliate page has
      // a full copy button with toast feedback.
    }
  }

  const handleLogout = async () => {
    await logout()
    window.location.href = '/login'
  }

  const applyTheme = (next: ThemeMode) => {
    setTheme(next)
    localStorage.setItem('theme-preference', next)
    const html = document.documentElement
    // Midnight stacks on .dark so all dark: Tailwind variants still apply.
    html.classList.toggle('dark', next === 'dark' || next === 'midnight')
    html.classList.toggle('midnight', next === 'midnight')
  }

  // Re-apply on mount so deep links / refreshes paint with the right palette
  // before React hydrates the rest of the chrome.
  React.useEffect(() => { applyTheme(theme) }, [])

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Build breadcrumb trail
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const breadcrumbs = pathSegments.length === 0
    ? [{ label: 'Dashboard', path: '/' }]
    : pathSegments.map((segment, index) => {
        const path = '/' + pathSegments.slice(0, index + 1).join('/')
        const parentPath = '/' + pathSegments.slice(0, index).join('/')
        return {
          label: routeNames[path] || prettySegment(segment, parentPath),
          path
        }
      })

  return (
    <>
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />

      {/* Switching overlay */}
      {switchingTo && (
        <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center transition-opacity duration-200">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium text-muted-foreground">Switching to {switchingTo}...</p>
          </div>
        </div>
      )}

      <header
      className={cn(
        'fixed z-30 h-16 border-b border-border/50 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 transition-[left,right,top] duration-200',
        isImpersonating ? 'top-10' : 'top-0',
        isMobile ? 'left-0' : (sidebarCollapsed ? 'left-16' : 'left-64')
      )}
      style={{ right: peekRailOpen ? `${PEEK_RAIL_WIDTH_PX}px` : 0 }}
    >
      <div className="flex h-full items-center justify-between px-3 md:px-6">
        {/* Mobile Menu & Logo & Breadcrumbs */}
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          {/* Mobile hamburger menu */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className="h-9 w-9 flex-shrink-0"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <Breadcrumb className="hidden md:flex">
            <BreadcrumbList>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  <BreadcrumbItem>
                    {index === breadcrumbs.length - 1 ? (
                      <BreadcrumbPage className="font-semibold">
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        onClick={() => navigate(crumb.path)}
                        className="cursor-pointer hover:text-brand-primary"
                      >
                        {crumb.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-1 md:gap-3">
          {/* Command Palette Trigger - hidden on small mobile */}
          <Button
            variant="outline"
            onClick={() => setCommandOpen(true)}
            className="relative h-9 w-9 p-0 hidden sm:flex xl:h-10 xl:w-72 xl:justify-start xl:px-4 xl:py-2 border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors duration-200"
          >
            <Search className="h-4 w-4 xl:mr-2 text-muted-foreground" />
            <span className="hidden xl:inline-flex text-sm text-muted-foreground/70">
              Search anything...
            </span>
            <kbd className="pointer-events-none absolute right-2 top-2 hidden h-6 select-none items-center gap-1 rounded-md border border-border/50 bg-muted/50 px-1.5 font-mono text-[10px] font-medium opacity-100 xl:flex">
              <span className="text-xs">⌘</span>K
            </kbd>
          </Button>

          {/* Notifications */}
          <NotificationDropdown />

          {/* Cross-org notifications — only shows for multi-org users with alerts */}
          {userOrgs.length > 1 && <CrossOrgNotifications />}

          {/* Theme — Light / Midnight / Dark segmented pill */}
          <div
            role="radiogroup"
            aria-label="Color theme"
            className="hidden sm:flex items-center gap-0.5 p-0.5 rounded-full border border-border/60 bg-muted/40"
          >
            {([
              { id: 'light',    label: 'Light',    icon: Sun },
              { id: 'midnight', label: 'Midnight', icon: Sparkles },
              { id: 'dark',     label: 'Dark',     icon: Moon },
            ] as const).map(({ id, label, icon: Icon }) => {
              const active = theme === id
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={label}
                  title={label}
                  onClick={() => applyTheme(id)}
                  className={`relative h-7 w-7 rounded-full flex items-center justify-center transition-all duration-200 [transition-timing-function:var(--ease-out,cubic-bezier(0.23,1,0.32,1))] press-scale ${
                    active
                      ? 'bg-card shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              )
            })}
          </div>

          {/* Mobile fallback — single cycle button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => applyTheme(theme === 'light' ? 'midnight' : theme === 'midnight' ? 'dark' : 'light')}
            className="h-10 w-10 sm:hidden press-scale"
            aria-label="Cycle theme"
            title={`Theme: ${theme}`}
          >
            {theme === 'light' && <Sun className="h-5 w-5" />}
            {theme === 'midnight' && <Sparkles className="h-5 w-5" />}
            {theme === 'dark' && <Moon className="h-5 w-5" />}
          </Button>

          {/* Account Center — unified user menu + account switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 gap-2 px-2 hover:bg-muted/40 focus-visible:ring-0 focus-visible:ring-offset-0 outline-none press-scale">
                <Avatar className="h-8 w-8 ring-2 ring-brand-periwinkle/30 ring-offset-2 ring-offset-background">
                  <AvatarImage src={agent?.avatarUrl} alt={agent?.name} />
                  <AvatarFallback className="bg-gradient-to-br from-brand-indigo to-brand-royal text-white text-sm font-semibold">
                    {getInitials(agent?.name || '')}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden lg:block text-left">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium leading-none">{agent?.name}</p>
                    {isSuperAdmin && (
                      <span className="inline-flex items-center rounded-md bg-[#ede9fe] dark:bg-violet-900/40 px-2 py-0.5 text-[10px] font-semibold text-violet-600 dark:text-violet-300">
                        Super Admin
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {location.pathname.startsWith('/admin')
                      ? 'The Castle'
                      : userOrgs.find(o => o.id === activeOrgId)?.name || agent?.role}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              {/* User Info */}
              <div className="px-3 py-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={agent?.avatarUrl} alt={agent?.name} />
                    <AvatarFallback className="bg-gradient-to-br from-brand-indigo to-brand-royal text-white text-sm font-semibold">
                      {getInitials(agent?.name || '')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold truncate">{agent?.name}</p>
                      {isSuperAdmin && (
                        <span className="inline-flex items-center rounded-md bg-[#ede9fe] dark:bg-violet-900/40 px-2 py-0.5 text-[10px] font-semibold text-violet-600 dark:text-violet-300">
                          Super Admin
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{agent?.email}</p>
                  </div>
                </div>
              </div>

              <DropdownMenuSeparator />

              {/* Switch Account section */}
              {(userOrgs.length > 0 || isPlatformAdmin) && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
                    Switch Account
                  </DropdownMenuLabel>

                  {isPlatformAdmin && (
                    <a
                      href="/admin"
                      onClick={handleSwitchToAdmin}
                      className="flex items-center justify-between cursor-pointer mx-1 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground outline-none transition-colors"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-brand-indigo to-brand-royal flex items-center justify-center flex-shrink-0">
                          <Shield className="h-4 w-4 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm">The Castle</p>
                          <p className="text-xs text-muted-foreground">Above every franchise</p>
                        </div>
                      </div>
                      {location.pathname.startsWith('/admin') && (
                        <Check className="h-4 w-4 text-brand-primary flex-shrink-0 ml-2" />
                      )}
                    </a>
                  )}

                  {userOrgs.map((org) => (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => handleSwitchOrg(org.id)}
                      className="flex items-center justify-between cursor-pointer mx-1 rounded-md"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{org.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {org.role === 'admin' ? 'Admin' : org.role === 'supervisor' ? 'Supervisor' : 'Member'}
                          </p>
                        </div>
                      </div>
                      {org.id === activeOrgId && !location.pathname.startsWith('/admin') && (
                        <Check className="h-4 w-4 text-brand-primary flex-shrink-0 ml-2" />
                      )}
                    </DropdownMenuItem>
                  ))}

                  <DropdownMenuSeparator />
                </>
              )}

              {affiliateInfo && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
                    Affiliate
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/affiliate')} className="mx-1 rounded-md">
                    <Sparkles className="mr-2 h-4 w-4" />
                    <span>Affiliate Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenAffiliateDemo} className="mx-1 rounded-md">
                    <PlayCircle className="mr-2 h-4 w-4" />
                    <span>Open Demo Workspace</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCopyAffiliateLink} className="mx-1 rounded-md">
                    <Copy className="mr-2 h-4 w-4" />
                    <span>Copy Affiliate Link</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              {/* Actions */}
              <DropdownMenuItem onClick={() => navigate(`/team/profile/${agent?.id}`)} className="mx-1 rounded-md">
                <User className="mr-2 h-4 w-4" />
                <span>My Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="mx-1 rounded-md">
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={handleLogout} className="mx-1 rounded-md text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
    </>
  )
}
